import React, { useContext, forwardRef, useRef, useEffect, useState } from "react";
import { RESOURCE_VIEW_CONFIG } from "../../other/constants/PlanView/ViewConfg.jsx";
import { EDITOR_KEYS } from "../../other/constants/EditorKeys.jsx";
import { canOpenEditor, debounce } from "../../other/utils/otherUtils.jsx";
import { BryntumGrid } from "@bryntum/schedulerpro-react";
import BryntumContext from "../context/bryntumContext.jsx";
import { processCellMenuItems } from "../../other/utils/gridCellMenuUtils.jsx";
import {GRANTS} from "../../other/constants/PrivilegeTypes.jsx";
import {WIDGET_KEYS} from "../../other/constants/WidgetKeys.jsx";
import { STORE_KEYS } from "../../other/constants/StoreKeys.jsx";
import { cellAvatarTooltipConfig, getOriginalRecord } from "../../other/utils/utils.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import { CopyToBoardDialog } from "@/react/components/CopyToBoardDialog";

const ResourcesGridView = forwardRef((props, ref) => {
  const gridRef = useRef(null);
  const context = useContext(BryntumContext);
  const { user, editorHandler, project, entityManager } = context;
  const [bulkCopyRecords, setBulkCopyRecords] = useState(null);

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.instance.features.cellMenu.processItems = processCellMenuItems({
        entityName: "Resource",
        editorHandler,
        grid: gridRef.current.instance,
        user,
        promptOnRemove: true,
        project,
        tagStoreKey: STORE_KEYS.resourceTagValuesStore,
        onEditEntity(record) {
          editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, getOriginalRecord(record), { resource: record });
        },
        onBulkCopyTo: (selectedRecords) => setBulkCopyRecords(selectedRecords),
      });
    }
  }, [editorHandler, user]);

  const onGridCellClick = debounce(({ column, record, event }) => {
    const originalRecord = getOriginalRecord(record);

    if (canOpenEditor(column, record) && event.detail === 1 && (column.isTagColumnBase || column.isTeamColumn || column.isAssignToColumn || column.isEligibleUserColumn || column.type === WIDGET_KEYS.columnResourceType)) {
        editorHandler.openEditor(EDITOR_KEYS.entityTagEditor, null, {
          entityRecord: originalRecord,
          event,
          column
        }, { by: "recordType", useExisting: true });
    }
  }, 300);

  return (
    <>
      {bulkCopyRecords && (
        <CopyToBoardDialog
          open={!!bulkCopyRecords}
          onOpenChange={(isOpen) => { if (!isOpen) setBulkCopyRecords(null); }}
          records={bulkCopyRecords}
          entityType={ENTITY_TYPES.RESOURCE}
        />
      )}
      <BryntumGrid
        {...props}
        {...RESOURCE_VIEW_CONFIG}
      disabled={user && !user.can(GRANTS.grant_four)}
      readOnly={user && !user.can(GRANTS.grant_four)}
      ref={(gridWidgetRef) => {
        ref(gridWidgetRef);
        gridRef.current = gridWidgetRef;
      }}
      onHeaderMenuItem={({ source, item }) => {
        if (item._menuId === "resource") {
          const newRecord = source.store.createRecord();
          editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, newRecord, { resource: newRecord });
        }
      }}
      flex="1 1 25%"
      onCellClick={onGridCellClick}
      onCellDblClick={({ record, column }) => {
        const originalRecord = getOriginalRecord(record);
        if (canOpenEditor(column, originalRecord)) {
          entityManager.openEntityManager({
            entityType: ENTITY_TYPES.RESOURCE,
            entityId: originalRecord.id
          });
        }
      }}
      cellTooltipFeature={cellAvatarTooltipConfig}
      />
    </>
  );
});

export default ResourcesGridView;
